import { CommonForm } from './_common-form.js';
export class APIForm extends CommonForm {
  constructor() {
    super();
    this._successHandler = null;
    this._errorsContainer = null;
  }
  connectedCallback() {
    super.connectedCallback();
    this._successHandler = this.getAttribute('data-success-handler') || 'redirect';
    this._errorsContainer = this._form.querySelector('[data-errors]');
  }
  disconnectedCallback() {
    super.disconnectedCallback();
  }
  handleSubmit(event) {
    event.preventDefault();
    if (!this._validator) {
      console.error('Validator not initialized');
      return;
    }
    this._validator.validate().then(() => {
      if (this._validator.isValid) {
        this._submitForm();
      } else {
        console.error('Form validation failed, submission prevented');
      }
    });
  }
  _submitForm() {
    const formData = new FormData(this._form);
    const endpoint = this._form.getAttribute('action') || this._getDefaultEndpoint();

    // Get nonce from component attribute or from form input
    const nonce = this.getAttribute('data-nonce') || this._form.querySelector('#nonce')?.value;

    // Prepare headers
    const headers = {};

    // Add nonce to headers if available
    if (nonce) {
      headers['X-WP-Nonce'] = nonce;
    }

    // Add Accept header
    headers['Accept'] = 'application/json';

    // Prepare fetch options
    const fetchOptions = {
      method: "POST",
      headers: headers,
      body: formData
    };

    // Only add credentials if specified in the component attribute
    if (this.hasAttribute('data-credentials')) {
      fetchOptions.credentials = this.getAttribute('data-credentials');
    }
    fetch(endpoint, fetchOptions).then(response => response.json()).then(data => this._handleResponse(data)).catch(error => this._handleError(error));
  }
  _getDefaultEndpoint() {
    throw new Error('Subclasses must implement _getDefaultEndpoint');
  }
  _handleResponse(data) {
    if (data.status === "success" || data.success === true) {
      this._handleSuccess(data);
    } else {
      this._handleApiErrors(data);
    }
  }
  _handleSuccess(data) {
    // Reset validation state using the built-in method
    if (this._validator) {
      this._validator.clearErrors();
    }

    // Still clear any general error container that might not be managed by the validator
    if (this._errorsContainer) {
      this._errorsContainer.innerHTML = '';
    }

    // Reset form fields if needed
    if (this.hasAttribute('data-reset-on-success') && this.getAttribute('data-reset-on-success') !== 'false') {
      this._form.reset();
    }

    // Now handle success redirects or modals
    if (this._successHandler === 'redirect') {
      const redirectUrl = this.getAttribute('data-success');
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.warn('No success URL specified in data-success attribute');
      }
    } else if (this._successHandler === 'modal') {
      const modalId = this.getAttribute('data-success');
      if (modalId) {
        const modalElement = document.getElementById(modalId);
        modalElement?.open?.();
      } else {
        console.warn('No success modal ID specified in data-success attribute');
      }
    }
  }
  _handleApiErrors(data) {
    console.error("API request failed:", data);
    this._clearErrorMessages();
    if (data && typeof data === 'object') {
      // Check for direct error message in various formats
      if (data.error) {
        this._showGeneralError(data.error);
        return;
      }

      // Check for message property (common in WordPress REST API responses)
      if (data.message) {
        this._showGeneralError(data.message);
        return;
      }

      // Check for code property with message (another common pattern)
      if (data.code && data.message) {
        this._showGeneralError(data.message);
        return;
      }
      this._processFieldErrors(data);
      this._showErrorSummary(data);
    } else {
      this._showGeneralError("Une erreur s'est produite");
    }
  }
  _handleError(error) {
    console.error("Error:", error);
    this._showGeneralError("Une erreur de connexion s'est produite");
  }
  _clearErrorMessages() {
    const errorLabels = this._form.querySelectorAll('.c-group__error');
    errorLabels.forEach(label => {
      label.textContent = '';
    });
    const fields = this._form.querySelectorAll('.is-invalid');
    fields.forEach(field => {
      field.classList.remove('is-invalid');
    });
  }
  _showGeneralError(message) {
    if (this._errorsContainer) {
      this._errorsContainer.innerHTML = `<span>${message}</span>`;
    } else {
      console.error(message);
    }
  }
  _processFieldErrors(data) {
    for (const [fieldName, errors] of Object.entries(data)) {
      if (Array.isArray(errors) && errors.length > 0) {
        const errorMessage = errors[0];
        const errorLabel = this._form.querySelector(`[data-error="${fieldName}"]`);
        if (errorLabel) {
          errorLabel.textContent = errorMessage;
          const field = this._form.querySelector(`[name="${fieldName}"]`);
          if (field) {
            field.classList.add('is-invalid');
          }
        }
      }
    }
  }
  _showErrorSummary(data) {
    if (!this._errorsContainer) return;
    this._errorsContainer.innerHTML = '';
    const errorHeading = document.createElement('h3');
    errorHeading.textContent = this._errorsContainer.getAttribute('data-title') || 'Veuillez corriger les erreurs suivantes :';
    this._errorsContainer.appendChild(errorHeading);
    for (const [fieldName, errors] of Object.entries(data)) {
      if (Array.isArray(errors) && errors.length > 0) {
        const field = this._form.querySelector(`[name="${fieldName}"]`);
        const errorItem = field ? document.createElement('label') : document.createElement('span');
        if (field) {
          errorItem.setAttribute('for', field.id || `${fieldName}-field`);
        }
        errorItem.textContent = errors[0];
        this._errorsContainer.appendChild(errorItem);
      }
    }
  }
}